import { type ReactNode, createContext, useContext } from "react";
import { useRootLoaderData } from "~/remix-lib";

type FeatureFlagContextType = {
  isEnabled: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

type FeatureFlagProps = {
  flag: string;
  children: ReactNode;
};

function FeatureFlagRoot({ flag, children }: FeatureFlagProps) {
  const { featureFlags } = useRootLoaderData();
  const isEnabled = featureFlags.includes(flag);

  return (
    <FeatureFlagContext.Provider value={{ isEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

function Enabled({ children }: { children: ReactNode }) {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "FeatureFlag.Enabled must be used within a FeatureFlag.Root",
    );
  }
  return context.isEnabled ? <>{children}</> : null;
}

function Disabled({ children }: { children: ReactNode }) {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "FeatureFlag.Disabled must be used within a FeatureFlag.Root",
    );
  }
  return !context.isEnabled ? <>{children}</> : null;
}

const FeatureFlag = {
  Root: FeatureFlagRoot,
  Enabled: Enabled,
  Disabled: Disabled,
};

export { FeatureFlag };
